import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { HeaderElbb } from 'pages/HomeElbb/components/Header';
import { StepIndicator } from 'components/ui/StepIndicator';
import Attachment from 'components/ui/Attachment';
import ModalWarning from 'components/ui/ModalWarning';
import { useModal } from 'context/ModalContext/Modal.context';
import { useAuth } from 'context/AuthContext';
import { useRule } from 'hooks/useRule';
import { BsCameraVideoFill } from 'react-icons/bs';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { atualizarIniciativa, inscricaoIniciativaElbb } from 'services/iniciativa-elbb.service';
import { registrarConfirmar, registrarEnviarVideo } from 'services/iniciativa-action.service';
import { clearRegistrationUser } from 'utils/fns';
import { uploadFile } from 'services/file.service';

const elbbSteps = [
  { icon: BsCameraVideoFill as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 1, activeColor: 'bg-pink-bb-2', iconColor: 'text-pink-bb-2' },
  { icon: IoCheckmarkCircle as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 2, activeColor: 'bg-pink-bb-2', iconColor: 'text-pink-bb-2' },
];

export function InscricaoIniciativaElbb() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const rule = useRule();
  const { showModal, closeModal } = useModal();

  const iniciativa = (location.state as any)?.iniciativa || null;

  const [videoUrl, setVideoUrl] = useState('');
  const [ruleUrl, setRuleUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  useEffect(() => {
    if (rule) setRuleUrl(rule.url);
  }, [rule]);

  const handlerSend = async () => {
    setIsSending(true);
    try {
      closeModal();
      if (!user || !iniciativa) return;

      const documento: any[] = [];
      if (videoUrl) {
        documento.push({ file: videoUrl, type: 'video' });
      }
      if (pdfFile) {
        const fileData = await uploadFile(pdfFile);
        documento.push({ file: fileData.url, nameFile: fileData.filename, type: 'file' });
      }

      const nomeGuerra = user.extra?.nome_guerra || user.name;
      const prefixo = user.detail?.prefixo || '';
      const inscricaoPlayer = `${nomeGuerra} (${user._id} - ${prefixo || '-'})`;

      const obj = {
        ...iniciativa.iniciativaOriginal,
      }

      delete obj.log;
      delete obj.m;
      delete obj.p;
      delete obj.target;

      await atualizarIniciativa({
        ...obj,
        documento,
        dt_inscricao: new Date(),
        status: 'inscrita',
        inscricao_player: inscricaoPlayer,
      });

      const currentPlayer = clearRegistrationUser(user._id);
      const body = {
        id_realizacao: id,
        title: iniciativa.titulo,
        dt_aceite: new Date(),
        rawPlayer: user._id,
        player: currentPlayer,
        player_name: user.name,
        documento,
      };

      await inscricaoIniciativaElbb(body, iniciativa.iniciativaOriginal);

      try {
        await registrarEnviarVideo(user._id);
        await registrarConfirmar(user._id);
      } catch (actionLogError) {
        console.warn('Falha ao registrar action_log da inscrição ELBB:', actionLogError);
      }

      navigate('/confirmacao-elbb');
    } catch (error) {
      console.error('Erro ao finalizar inscrição ELBB:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleProximaEtapa = () => {
    showModal(
      'accept-terms',
      <ModalWarning
        rule={ruleUrl}
        title="Sua inscrição está quase pronta!"
        subTitle="Confirme abaixo algumas informações importantes antes de finalizá-la."
        needInfo={true}
        needRule={true}
        description={
          <div className="flex flex-col gap-3 text-base">
            <p>Declaro que li e concordo com os termos estabelecidos no Regulamento do Prêmio ELBB.</p>
            <p>
              Estou ciente sobre os prazos de inscrição, requisitos de participação, critérios de avaliação e os meus direitos e deveres como participante do Prêmio ELBB.
            </p>
            <p>Comprometo-me a fornecer informações precisas e completas, além de seguir todas as regras e regulamentos da premiação.</p>
            <p>Autorizo a organização a usar minha imagem em fotos ou vídeos, bem como meus dados pessoais, para fins de divulgação.</p>
            <p>
              Tenho conhecimento de que será realizada a verificação dos requisitos de impedimentos, com base na situação apurada no primeiro dia útil após o encerramento das inscrições.
            </p>
          </div>
        }
        infoLabel="Li e estou de acordo com as informações disponibilizadas acima."
        ruleLabel={
          <>
            Li e estou de acordo com o{' '}
            <Link to="/regulamento-elbb" target="_blank" className="underline text-blue-bb-1 hover:no-underline">
              regulamento do Prêmio ELBB
            </Link>
            .
          </>
        }
        acceptTextClassName="text-base"
        showRuleLink={false}
        btnText="Finalizar inscrição"
        onBackButton={closeModal}
        hasBackButton={true}
        onClose={handlerSend}
      />,
      'accept-terms'
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <HeaderElbb currentPage="iniciativas" />

      {/* Step indicator */}
      <div className="bg-gray-100 py-6 mt-8">
        <div className="max-w-[1200px] mx-auto px-4">
          <StepIndicator currentStep={1} steps={elbbSteps} progressColor="bg-pink-bb-2" />
        </div>
      </div>

      <main className="px-4 py-8 pb-16">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6" tabIndex={0}>
            Anexe o vídeo sobre a iniciativa.
          </h2>

          <div className="flex items-center gap-4 border border-pink-bb-2 rounded-xl p-4 mb-8 relative overflow-hidden">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center">
              <BsCameraVideoFill className="text-pink-bb w-20 h-20 absolute -left-8" aria-hidden="true" />
            </div>
            <div className="text-base text-gray-600 leading-relaxed flex flex-col gap-3">
              <p tabIndex={0}>
                Para seguir para a próxima etapa, é obrigatório anexar um vídeo pitch de no máximo 3 minutos de duração, respondendo a seguinte pergunta norteadora: "Por que essa iniciativa foi fundamental para o atingimento da Perspectiva ECBB? <br></br>
                Copie o link do vídeo e cole no campo abaixo. <br></br>
                O anexo de um documento em formato PDF é opcional e pode complementar a apresentação da iniciativa.
              </p>
            </div>
          </div>

          {/* Attachment */}
          <div className="mb-10">
            <p className="text-sm font-semibold text-gray-700 mb-3" tabIndex={0}>Anexar link de vídeo</p>
            <Attachment
              onFileSelect={(file) => setPdfFile(file)}
              onVideoSelect={setVideoUrl}
              onProgressComplete={() => {}}
              allowedTypes={['application/pdf']}
              maxFizeSize={3}
            />
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-end gap-4">
            <button
              className="bg-transparent border border-gray-300 text-gray-600 rounded-lg px-8 py-3 text-sm font-bold cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => navigate(-1)}
            >
              VOLTAR
            </button>
            <button
              disabled={!videoUrl || isSending}
              onClick={handleProximaEtapa}
              className="bg-[#f5c518] text-[#1e3a5f] border-0 rounded-lg px-8 py-3 text-sm font-extrabold cursor-pointer hover:bg-[#e6b800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              IR PARA A PRÓXIMA ETAPA
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
